import { Suspense, lazy, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate as RRNavigate, useLocation, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ResearchGuard } from "@/components/research/ResearchGuard";
import { useAuth } from "@/contexts/AuthContext";
import { ComplianceProvider } from "@/contexts/ComplianceContext";
import { LocaleProvider, SUPPORTED_LOCALES, getStoredLocale } from "@/contexts/LocaleContext";
import { FEATURE_FLAGS, isFeatureEnabled } from "@/lib/feature-flags";
import { hasSeenWelcome } from "@/lib/discovery/welcome";
import {
  resolveLocaleSync,
  resolveLocaleAsync,
  hasExplicitLocaleChoice,
} from "@/lib/locale-resolution";
import { getDetectedGeoCountry } from "@/lib/compliance/geo-ip";
import { AgeGate } from "@/components/AgeGate";
import { safeFastTrackDestination } from "@/lib/onboarding/fast-track-destination";
import { ErrorBoundary, GlobalErrorReportListener } from "@/components/ErrorBoundary";

/** Wraps children with <AgeGate> but skips it on consumer onboarding route */
function AgeGateWrapper({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  // Skip global age gate on consumer onboarding — the shell has its own
  const isOnboarding = /\/app\/onboarding\/?$/.test(pathname);
  const isTradeOnboarding = /\/trade\/(onboarding|join)\/?$/.test(pathname);
  // Fast Track asks country + age itself and then satisfies the site gate.
  const isFastTrack = /\/get-started\/?$/.test(pathname);
  // QR redirect and Patron invitation lead straight into Fast Track (which asks age);
  // neither shows cigar content, so a scan is not stopped by the full date-of-birth gate.
  const isQrEntry = /\/(q|patron\/invite)\/[^/]+\/?$/.test(pathname);
  const isQrSignIn = /\/auth\/?$/.test(pathname) && safeFastTrackDestination(new URLSearchParams(search).get("redirect")) !== null;
  if (isOnboarding || isTradeOnboarding || isFastTrack || isQrEntry || isQrSignIn) return <>{children}</>;
  return <AgeGate>{children}</AgeGate>;
}
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { ReferralCapture } from "@/components/referral/ReferralCapture";
import { TestAccountBanner } from "@/components/TestAccountBanner";

import { DynamicFavicon } from "@/components/DynamicFavicon";
import { DynamicManifest } from "@/components/DynamicManifest";
import { Navigate } from "@/lib/locale-link";
import { getLastDomain } from "@/hooks/usePostLoginRedirect";
import { resolveLandingRoute } from "@/lib/post-login-resolver";
import { useAccountCapabilities } from "@/hooks/useAccountCapabilities";
import { useRouteRegistry } from "@/hooks/useRouteRegistry";
import { validateRouteRegistry, getDisabledPaths } from "@/lib/route-validator";

import PublicLayout from "@/components/layout/PublicLayout";
import HomeV2 from "./pages/HomeV2";
const DiscoveryHome = lazy(() => import("./pages/DiscoveryHome"));

/* ── Research Operations (isolated workspace) ── */
const ResearchLayoutShell = lazy(() => import("./components/research/ResearchLayout"));
const ResearchLogin = lazy(() => import("./pages/research/ResearchLogin"));
const ResearchDashboard = lazy(() => import("./pages/research/ResearchDashboard"));
const ResearchProfilePage = lazy(() => import("./pages/research/ResearchProfile"));
const ResearchPasswordPage = lazy(() => import("./pages/research/ResearchPassword"));
const ResearchHelpPage = lazy(() => import("./pages/research/ResearchHelp"));
const ResearchInvitePage = lazy(() => import("./pages/research/ResearchInvite"));
const ResearchAccessDeniedPage = lazy(() => import("./pages/research/ResearchAccessDenied"));
const ResearchProjectsPage = lazy(() => import("./pages/research/ResearchProjects"));
const ResearchProjectDetailPage = lazy(() => import("./pages/research/ResearchProjectDetail"));
const ResearchReviewPage = lazy(() => import("./pages/research/ResearchReview"));
const ResearchBusinessWorkstreamPage = lazy(() => import("./pages/research/ResearchBusinessWorkstream"));
const ResearchRegistryWorkPage = lazy(() => import("./pages/research/ResearchRegistryWork"));
const ResearchMyWorkPage = lazy(() => import("./pages/research/ResearchMyWork"));
const ResearchWorkItemPage = lazy(() => import("./pages/research/ResearchWorkItem"));
const ResearchAiRegistryPage = lazy(() => import("./pages/research/ResearchAiRegistry"));
const ResearchUsersPage = lazy(() => import("./pages/research/ResearchUsers"));
const WhyVitolier = lazy(() => import("./pages/WhyVitolier"));
const UniversalSearch = lazy(() => import("./pages/UniversalSearch"));
const Welcome = lazy(() => import("./pages/Welcome"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const HomeLegacy = lazy(() => import("./pages/Index"));



const ConsumerLayout = lazy(() => import("@/components/layout/ConsumerLayout"));
const TradeLayout = lazy(() => import("@/components/layout/TradeLayout"));
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));
const BrandOwnerLayout = lazy(() => import("@/components/layout/BrandOwnerLayout"));

const BrowseCigars = lazy(() => import("./pages/BrowseCigars"));
const BrowseBrands = lazy(() => import("./pages/BrowseBrands"));
const MyBrandsPortfolio = lazy(() => import("./pages/MyBrandsPortfolio"));
const MyCigarsPortfolio = lazy(() => import("./pages/MyCigarsPortfolio"));
const BrowseVitolas = lazy(() => import("./pages/BrowseVitolas"));
const BrowseManufacturers = lazy(() => import("./pages/BrowseManufacturers"));
const BrowseRetailers = lazy(() => import("./pages/BrowseRetailers"));
const BrowseDistributors = lazy(() => import("./pages/BrowseDistributors"));
const BrowseLounges = lazy(() => import("./pages/BrowseLounges"));
const BrowseClubs = lazy(() => import("./pages/BrowseClubs"));
const BrowseAssociations = lazy(() => import("./pages/BrowseAssociations"));
const ClubDetail = lazy(() => import("./pages/ClubDetail"));
const AssociationDetail = lazy(() => import("./pages/AssociationDetail"));
const CigarDetail = lazy(() => import("./pages/CigarDetail"));
const CigarProfile = lazy(() => import("./pages/CigarProfile"));
const BrandDetail = lazy(() => import("./pages/BrandDetail"));
const VitolaDetail = lazy(() => import("./pages/VitolaDetail"));
const ManufacturerDetail = lazy(() => import("./pages/ManufacturerDetail"));
const ChannelDetail = lazy(() => import("./pages/ChannelDetail"));
const Plans = lazy(() => import("./pages/Plans"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const PastEventsPage = lazy(() => import("./pages/PastEventsPage"));

const CulturePage = lazy(() => import("./pages/CulturePage"));
const CultureArticle = lazy(() => import("./pages/CultureArticle"));
const Cigar101Hub = lazy(() => import("./pages/Cigar101Hub"));
const LearnHub = lazy(() => import("./pages/LearnHub"));

const Cigar101Guide = lazy(() => import("./pages/Cigar101Guide"));
const Cigar101Glossary = lazy(() => import("./pages/Cigar101Glossary"));
const Cigar101TermDetail = lazy(() => import("./pages/Cigar101TermDetail"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const BrandGuidelines = lazy(() => import("./pages/BrandGuidelines"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventRegister = lazy(() => import("./pages/EventRegister"));
const MyEvents = lazy(() => import("./pages/MyEvents"));
const Auth = lazy(() => import("./pages/Auth"));
const Signup = lazy(() => import("./pages/Signup"));
const MembershipSuccess = lazy(() => import("./pages/MembershipSuccess"));
const RedeemDiscovery = lazy(() => import("./pages/RedeemDiscovery"));
const PatronActivate = lazy(() => import("./pages/PatronActivate"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ProfileRedirect = lazy(() => import("./components/ProfileRedirect"));
const ProfileEdit = lazy(() => import("./pages/ProfileEdit"));
const ConsumerProfile = lazy(() => import("./pages/ConsumerProfile"));
const ConsumerHome = lazy(() => import("./pages/ConsumerHome"));
const ConsumerHomeLegacy = lazy(() => import("@/pages/ConsumerHomeLegacy"));
const ConsumerHomePersonal = lazy(() => import("./pages/ConsumerHomePersonal"));
const MyContent = lazy(() => import("./pages/MyContent"));
const AccountHub = lazy(() => import("./pages/account/AccountHub"));
const AccountSecurity = lazy(() => import("./pages/account/AccountSecurity"));
const AccountLocation = lazy(() => import("./pages/account/AccountLocation"));
const AccountShare = lazy(() => import("./pages/account/AccountShare"));
const MyQrCodes = lazy(() => import("./pages/account/MyQrCodes"));
const AdminQrReports = lazy(() => import("./pages/AdminQrReports"));
const AccountDelete = lazy(() => import("./pages/account/AccountDelete"));
const AccountFollowing = lazy(() => import("./pages/account/AccountFollowing"));
const TradeContent = lazy(() => import("./pages/TradeContent"));
const AdminContentSharing = lazy(() => import("./pages/admin/AdminContentSharing"));

const SocialFeed = lazy(() => import("./pages/SocialFeed"));
const SocialPost = lazy(() => import("./pages/SocialPost"));
const SocialHashtag = lazy(() => import("./pages/SocialHashtag"));
const DiscoverWorld = lazy(() => import("./pages/DiscoverWorld"));
const DiscoverCountry = lazy(() => import("./pages/DiscoverCountry"));
const DashboardSettings = lazy(() => import("./pages/DashboardSettings"));
const DashboardReviews = lazy(() => import("./pages/DashboardReviews"));
const DashboardHumidors = lazy(() => import("./pages/DashboardHumidors"));
const DashboardSmokingLog = lazy(() => import("./pages/DashboardSmokingLog"));
const DashboardInsights = lazy(() => import("./pages/DashboardInsights"));
const CigarJournal = lazy(() => import("./pages/CigarJournal"));
const AppSupport = lazy(() => import("./pages/AppSupport"));
const SupportNewBug = lazy(() => import("./pages/SupportNewBug"));
const SupportNewFeature = lazy(() => import("./pages/SupportNewFeature"));
const SupportFeatureDetail = lazy(() => import("./pages/SupportFeatureDetail"));
const SupportBugDetail = lazy(() => import("./pages/SupportBugDetail"));
const Notifications = lazy(() => import("./pages/Notifications"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const Communities = lazy(() => import("./pages/Communities"));
const CommunityDetail = lazy(() => import("./pages/CommunityDetail"));
const Trending = lazy(() => import("./pages/Trending"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const StoreLayout = lazy(() => import("./pages/store/StoreLayout"));
const StoreHome = lazy(() => import("./pages/store/StoreHome"));
const StoreBooks = lazy(() => import("./pages/store/StoreBooks"));
const StoreBookDetail = lazy(() => import("./pages/store/StoreBookDetail"));
const StoreProduct = lazy(() => import("./pages/store/StoreProduct"));
const StoreVitolierCollection = lazy(() => import("./pages/store/StoreVitolierCollection"));
const StoreBrandMerchandise = lazy(() => import("./pages/store/StoreBrandMerchandise"));

const BrandPortalDashboard = lazy(() => import("./pages/BrandPortalDashboard"));
const BrandPortalContent = lazy(() => import("./pages/BrandPortalContent"));
const BrandPortalCigars = lazy(() => import("./pages/BrandPortalCigars"));
const BrandPortalMedia = lazy(() => import("./pages/BrandPortalMedia"));
const BrandPortalEvents = lazy(() => import("./pages/BrandPortalEvents"));
const BrandPortalRelationships = lazy(() => import("./pages/BrandPortalRelationships"));
const BrandPortalCigarRelationships = lazy(() => import("./pages/BrandPortalCigarRelationships"));
const BrandPortalCorrections = lazy(() => import("./pages/BrandPortalCorrections"));
const BrandPortalCorrectionDetail = lazy(() => import("./pages/BrandPortalCorrectionDetail"));
const BrandPortalAnalytics = lazy(() => import("./pages/BrandPortalAnalytics"));
const BrandPortalSubscription = lazy(() => import("./pages/BrandPortalSubscription"));
const RetailerDashboard = lazy(() => import("./pages/RetailerDashboard"));
const TradeVenues = lazy(() => import("./pages/TradeVenues"));
const TradeEventsWorkspace = lazy(() => import("./pages/trade/events/TradeEventsWorkspace"));
const TradeEnquiriesWorkspace = lazy(() => import("./pages/trade/enquiries/TradeEnquiriesWorkspace"));
const TradeEnquiryDetail = lazy(() => import("./pages/trade/enquiries/TradeEnquiryDetail"));
const TradeEventEditor = lazy(() => import("./pages/trade/events/TradeEventEditor"));
const TradeVenueManage = lazy(() => import("./pages/TradeVenueManage"));
const TradeClaims = lazy(() => import("./pages/TradeClaims"));
const TradeClaim = lazy(() => import("./pages/TradeClaim"));
const TradeClaimStatus = lazy(() => import("./pages/TradeClaimStatus"));
// Legacy brand onboarding routes retired in Phase A — redirected to Trade spine.
const Onboarding = lazy(() => import("./pages/Onboarding"));
const TradeOnboarding = lazy(() => import("./pages/TradeOnboarding"));
const TradeDashboard = lazy(() => import("./pages/TradeDashboard"));
const TradeIntelligenceCentre = lazy(() => import("./pages/TradeIntelligenceCentre"));
const TradeRelationshipIntelligence = lazy(() => import("./pages/TradeRelationshipIntelligence"));
const TradeOrganisation = lazy(() => import("./pages/TradeOrganisation"));
const TradeInvitationAccept = lazy(() => import("./pages/TradeInvitationAccept"));
const TradeCrm = lazy(() => import("./pages/TradeCrm"));
const TradeEntityManage = lazy(() => import("./pages/TradeEntityManage"));
// Business Profile Shell — Phase 1 additive scaffold.
// See docs/business-profile-framework/PARITY_MATRIX.md. Legacy routes
// (/trade/brands, /trade/venues, /trade/entity, /trade/organisation) remain
// authoritative and untouched.
const TradeManageShell = lazy(() => import("./pages/TradeManageShell"));
const TradeDiscovery = lazy(() => import("./pages/TradeDiscovery"));
const TradePublicProfile = lazy(() => import("./pages/TradePublicProfile"));
const MessagesInbox = lazy(() => import("./pages/dm/MessagesInbox"));
const ConversationView = lazy(() => import("./pages/dm/ConversationView"));
const NewConversation = lazy(() => import("./pages/dm/NewConversation"));
const NewSession = lazy(() => import("./pages/dm/NewSession"));
const SessionDetail = lazy(() => import("./pages/dm/SessionDetail"));

const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const TradeJoinRouter = lazy(() => import("./pages/TradeJoinRouter"));
const TradeJoinOrganisation = lazy(() => import("./pages/TradeJoinOrganisation"));
const TradeJoinReferral = lazy(() => import("./pages/TradeJoinReferral"));
const TradeJoinBrand = lazy(() => import("./pages/TradeJoinBrand"));
const TradeRegisterRouter = lazy(() => import("./pages/TradeRegisterRouter"));
const TradeSignin = lazy(() => import("./pages/TradeSignin"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AccountSetup = lazy(() => import("./pages/AccountSetup"));
const TradeSelectOrganisation = lazy(() => import("./pages/TradeSelectOrganisation"));
const TradeSelectPlan = lazy(() => import("./pages/TradeSelectPlan"));
const TradeInvitationsReview = lazy(() => import("./pages/TradeInvitationsReview"));
const TradeBrands = lazy(() => import("./pages/TradeBrands"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const AppOnboarding = lazy(() => import("./pages/AppOnboarding"));
const ConsumerLanding = lazy(() => import("./pages/ConsumerLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Discover = lazy(() => import("./pages/Discover"));
const RegionDetail = lazy(() => import("./pages/RegionDetail"));
const BusinessPreview = lazy(() => import("./pages/BusinessPreview"));
const TradePreview = lazy(() => import("./pages/TradePreview"));
const WhyJoin = lazy(() => import("./pages/WhyJoin"));
const Join = lazy(() => import("./pages/Join"));
const Share = lazy(() => import("./pages/Share"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const QrLanding = lazy(() => import("./pages/QrLanding"));
const PatronInvite = lazy(() => import("./pages/PatronInvite"));

const VitolierPreview = lazy(() => import("./pages/VitolierPreview"));
const HelpFaq = lazy(() => import("./pages/HelpFaq"));
const BrowseCigarLines = lazy(() => import("./pages/BrowseCigarLines"));
const CigarLineDetail = lazy(() => import("./pages/CigarLineDetail"));
const LegacyCollectionRedirect = lazy(() => import("./pages/LegacyCollectionRedirect"));
const CigarAtlas = lazy(() => import("./pages/CigarAtlas"));
const CigarAtlasMap = lazy(() => import("./pages/CigarAtlasMap"));
const CigarAtlasSuggest = lazy(() => import("./pages/CigarAtlasSuggest"));
const CigarAtlasPlaceDetail = lazy(() => import("./pages/CigarAtlasPlaceDetail"));
const CigarAtlasCollectionDetail = lazy(() => import("./pages/CigarAtlasCollectionDetail"));
const CigarAtlasCityCollection = lazy(() => import("./pages/CigarAtlasCityCollection"));


const AdminCulture = lazy(() => import("./pages/AdminCulture"));
const AdminCigar101 = lazy(() => import("./pages/AdminCigar101"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminWorkHub = lazy(() => import("./pages/AdminWorkHub"));
const AdminWorkHubMine = lazy(() => import("./pages/AdminWorkHubMine"));
const AdminWorkHubTemplates = lazy(() => import("./pages/AdminWorkHubTemplates"));
const AdminNews = lazy(() => import("./pages/AdminNews"));
const AdminStore = lazy(() => import("./pages/admin/AdminStore"));
const AdminVitolierClub = lazy(() => import("./pages/admin/AdminVitolierClub"));
const VitolierClubLanding = lazy(() => import("./pages/club/VitolierClubLanding"));
const ClubMemberLayout = lazy(() => import("./pages/club/ClubMemberLayout"));
const ClubOverview = lazy(() => import("./pages/club/ClubOverview"));
const ClubCardPage = lazy(() => import("./pages/club/ClubCardPage"));
const ClubBenefitsPage = lazy(() => import("./pages/club/ClubBenefitsPage"));
const ClubEventsPage = lazy(() => import("./pages/club/ClubEventsPage"));
const ClubChaptersPage = lazy(() => import("./pages/club/ClubChaptersPage"));
const ClubHistoryPage = lazy(() => import("./pages/club/ClubHistoryPage"));
const ClubPreferencesPage = lazy(() => import("./pages/club/ClubPreferencesPage"));
const TradeClubVerification = lazy(() => import("./pages/trade/TradeClubVerification"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSubmissions = lazy(() => import("./pages/AdminSubmissions"));
const AdminSubmissionDetail = lazy(() => import("./pages/AdminSubmissionDetail"));
const AdminBrands = lazy(() => import("./pages/AdminBrands"));
const AdminBrandHierarchy = lazy(() => import("./pages/AdminBrandHierarchy"));
const AdminCigarsPage = lazy(() => import("./pages/AdminCigars"));
const AdminCigarRegistry = lazy(() => import("./pages/AdminCigarRegistry"));
const AdminManufacturers = lazy(() => import("./pages/AdminManufacturers"));
const AdminVitolas = lazy(() => import("./pages/AdminVitolas"));
const AdminCountries = lazy(() => import("./pages/AdminCountries"));
const AdminCountryContributionsPage = lazy(() => import("./pages/AdminCountryContributions"));
const MyContributions = lazy(() => import("./pages/MyContributions"));
const AdminBusinesses = lazy(() => import("./pages/AdminBusinesses"));
const AdminBusinessDetail = lazy(() => import("./pages/AdminBusinessDetail"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminExternalReviews = lazy(() => import("./pages/AdminExternalReviews"));
const AdminExternalReviewDetail = lazy(() => import("./pages/AdminExternalReviewDetail"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminClaims = lazy(() => import("./pages/AdminClaims"));
const AdminOwnership = lazy(() => import("./pages/AdminOwnership"));
const AdminDuplicates = lazy(() => import("./pages/AdminDuplicates"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminTaxonomy = lazy(() => import("./pages/AdminTaxonomy"));
const AdminRetailers = lazy(() => import("./pages/AdminRetailers"));
const AdminLounges = lazy(() => import("./pages/AdminLounges"));
const AdminDistributors = lazy(() => import("./pages/AdminDistributors"));
const AdminRelationships = lazy(() => import("./pages/AdminRelationships"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminConsumers = lazy(() => import("./pages/AdminConsumers"));
const AdminConsumerDetail = lazy(() => import("./pages/AdminConsumerDetail"));
const AdminCrmContactNew = lazy(() => import("./pages/AdminCrmContactNew"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminImports = lazy(() => import("./pages/AdminImports"));
const AdminVersionHistory = lazy(() => import("./pages/AdminVersionHistory"));
const AdminDataQuality = lazy(() => import("./pages/AdminDataQuality"));
const AdminPlatformInsights = lazy(() => import("./pages/AdminPlatformInsights"));
const AdminMedia = lazy(() => import("./pages/AdminMedia"));
const AdminMediaHealth = lazy(() => import("./pages/AdminMediaHealth"));
const AdminJobHealth = lazy(() => import("./pages/AdminJobHealth"));
const AdminRetailerClaims = lazy(() => import("./pages/AdminRetailerClaims"));
const AdminRetailerMonetisation = lazy(() => import("./pages/AdminRetailerMonetisation"));
const AdminBilling = lazy(() => import("./pages/AdminBilling"));
const AdminMemberships = lazy(() => import("./pages/AdminMemberships"));
const AdminSubscribersDashboard = lazy(() => import("./pages/AdminSubscribersDashboard"));
const AdminSubscribers = lazy(() => import("./pages/AdminSubscribers"));
const AdminBrandSubmissions = lazy(() => import("./pages/AdminBrandSubmissions"));
const AdminBrandClaims = lazy(() => import("./pages/AdminBrandClaims"));
const AdminCrmDashboard = lazy(() => import("./pages/AdminCrmDashboard"));
const AdminCrmPipeline = lazy(() => import("./pages/AdminCrmPipeline"));
const AdminCrmBrandDetail = lazy(() => import("./pages/AdminCrmBrandDetail"));
const AdminCrmRequests = lazy(() => import("./pages/AdminCrmRequests"));
const AdminCrmCommand = lazy(() => import("./pages/AdminCrmCommand"));
const AdminSitemap = lazy(() => import("./pages/AdminSitemap"));
const AdminAppIcons = lazy(() => import("./pages/AdminAppIcons"));
const AdminFirecrawlImport = lazy(() => import("./pages/AdminFirecrawlImport"));
const AdminRegulatory = lazy(() => import("./pages/AdminRegulatory"));
const AdminComplianceModeration = lazy(() => import("./pages/AdminComplianceModeration"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const AdminCigarLines = lazy(() => import("./pages/AdminCigarLines"));
const AdminRegistryApprovals = lazy(() => import("./pages/AdminRegistryApprovals"));
const AdminRegistryCorrections = lazy(() => import("./pages/AdminRegistryCorrections"));
const AdminCigarAtlas = lazy(() => import("./pages/AdminCigarAtlas"));
const AdminCigarAtlasImport = lazy(() => import("./pages/AdminCigarAtlasImport"));
const AdminCigarAtlasFirecrawlImport = lazy(() => import("./pages/AdminCigarAtlasFirecrawlImport"));
const AdminCigarAtlasExcelImport = lazy(() => import("./pages/AdminCigarAtlasExcelImport"));
const AdminCigarAtlasCollections = lazy(() => import("./pages/AdminCigarAtlasCollections"));
const AdminCigarAtlasCityCollections = lazy(() => import("./pages/AdminCigarAtlasCityCollections"));

const AdminCigarAtlasReferences = lazy(() => import("./pages/AdminCigarAtlasReferences"));
const AdminCigarAtlasIntelligenceReview = lazy(() => import("./pages/AdminCigarAtlasIntelligenceReview"));
// 5C — Admin & Operations Audit additions
const AdminSubmissionsHub = lazy(() => import("./pages/AdminSubmissionsHub"));
const AdminClaimsHub = lazy(() => import("./pages/AdminClaimsHub"));
const AdminModerationHub = lazy(() => import("./pages/AdminModerationHub"));
const AdminInsightsHub = lazy(() => import("./pages/AdminInsightsHub"));
const AdminSubscribersHub = lazy(() => import("./pages/AdminSubscribersHub"));
const AdminCrmHub = lazy(() => import("./pages/AdminCrmHub"));
const AdminWorkHubUnified = lazy(() => import("./pages/AdminWorkHubUnified"));
const AdminOrganisations = lazy(() => import("./pages/AdminOrganisations"));
const AdminOrganisationDetail = lazy(() => import("./pages/AdminOrganisationDetail"));
const AdminBusinessesConsole = lazy(() => import("./pages/AdminBusinessesConsole"));
const UnifiedBusinessProfilePage = lazy(() => import("./pages/UnifiedBusinessProfilePage"));
const BusinessManagementShell = lazy(() => import("./pages/BusinessManagementShell"));
const AdminSites = lazy(() => import("./pages/AdminSites"));
const AdminEntitlements = lazy(() => import("./pages/AdminEntitlements"));
const AdminEmailOps = lazy(() => import("./pages/AdminEmailOps"));
const AdminNotificationsBroadcast = lazy(() => import("./pages/AdminNotificationsBroadcast"));
const AdminSecurityFindings = lazy(() => import("./pages/AdminSecurityFindings"));
const AdminUsersRoles = lazy(() => import("./pages/AdminUsersRoles"));
const AdminClassificationReview = lazy(() => import("./pages/AdminClassificationReview"));
const VitolierChatbotButton = lazy(() =>
  import("@/components/VitolierChatbot").then((m) => ({ default: m.VitolierChatbot })),
);

function LazyVitolierChatbot() {
  const [enabled, setEnabled] = useState(false);
  const [defaultOpen, setDefaultOpen] = useState(false);

  useEffect(() => {
    const open = () => {
      setDefaultOpen(true);
      setEnabled(true);
    };
    const preload = () => setEnabled(true);
    window.addEventListener("vitolier:open-chat", open, { once: true });
    window.addEventListener("keydown", preload, { once: true });
    return () => {
      window.removeEventListener("vitolier:open-chat", open);
      window.removeEventListener("keydown", preload);
    };
  }, []);

  if (!enabled) {
    return (
      <button
        onClick={() => {
          setDefaultOpen(true);
          setEnabled(true);
        }}
        className="fixed bottom-6 right-6 z-50 hidden md:flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
        aria-label="Open Vitolier Guide"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        </svg>
      </button>
    );
  }
  return <VitolierChatbotButton defaultOpen={defaultOpen} />;
}

/**
 * Redirect bare paths (no locale prefix) to the best-matching locale.
 *
 * Priority: explicit user choice → profile language → cached geo →
 * browser language → English. When no signal is cached we briefly
 * await the geo edge function (bounded ~2.5s) so first-time visitors
 * from supported regions land on their language immediately.
 */
function LocaleRedirect() {
  const location = useLocation();
  const { user } = useAuth();
  const profileLanguage = (user?.user_metadata?.preferred_language as string | undefined) ?? null;
  const [resolved, setResolved] = useState<string | null>(() => {
    // Use whatever we can decide synchronously first.
    const sync = resolveLocaleSync({ profileLanguage });
    // If the sync result is just the default fallback AND we have no cached
    // signal at all, defer the redirect until async geo resolves.
    if (sync !== "en") return sync;
    if (hasExplicitLocaleChoice()) return sync;
    if (getDetectedGeoCountry()) return sync;
    return null;
  });

  useEffect(() => {
    if (resolved) return;
    let cancelled = false;
    resolveLocaleAsync({ profileLanguage }).then((loc) => {
      if (!cancelled) setResolved(loc);
    });
    return () => {
      cancelled = true;
    };
  }, [resolved, profileLanguage]);

  if (!resolved) return null;
  return <RRNavigate to={`/${resolved}${location.pathname}${location.search}${location.hash}`} replace />;
}

function LocaleHomeEntry() {
  const { user, loading: authLoading, isBusiness, isAdmin, isConsumer } = useAuth();
  const { capabilities, isLoading: capsLoading } = useAccountCapabilities();

  // Never redirect to the welcome screen before the session is known: a
  // returning signed-in visitor must not flash an interstitial.
  if (authLoading && !user) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background text-muted-foreground text-sm">
        Loading Vitolier…
      </div>
    );
  }

  if (user) {
    if (capsLoading) {
      // Legacy-role fast path so consumers/admins don't flash a placeholder;
      // trade-only identities (no legacy roles) get a branded loading shell
      // instead of a blank screen.
      const hasBusinessAccess = isBusiness || isAdmin;
      const prefersTrade = hasBusinessAccess && isConsumer ? getLastDomain() === "trade" : false;
      if (isConsumer && !hasBusinessAccess) return <Navigate to="/app/home" replace />;
      if (hasBusinessAccess && (!isConsumer || prefersTrade)) return <Navigate to="/trade/dashboard" replace />;
      if (hasBusinessAccess && isConsumer && !prefersTrade) return <Navigate to="/app/home" replace />;
      return (
        <div className="min-h-screen-safe flex items-center justify-center bg-background text-muted-foreground text-sm">
          Preparing your Vitolier account…
        </div>
      );
    }

    const dest = resolveLandingRoute(capabilities, { lastDomain: getLastDomain() });
    return <Navigate to={dest} replace />;
  }

  // Phase 2: signed-out visitors land on the Public Discovery Home. First-time
  // visitors see the welcome screen once. The previous landing page is preserved
  // unchanged at /why-vitolier, and turning the flag off restores it here.
  if (!isFeatureEnabled(FEATURE_FLAGS.discoveryHome)) return <HomeV2 />;
  if (!hasSeenWelcome()) return <Navigate to="/welcome" replace />;
  return <DiscoveryHome />;
}

/**
 * Phase 3 — signed-in Consumer Home. The personalised Home is served behind the
 * `signed_in_home` flag; the previous dashboard stays available unchanged at
 * `/app/home-legacy` and returns here when the flag is turned off.
 */
function ConsumerHomeEntry() {
  if (!isFeatureEnabled(FEATURE_FLAGS.signedInHome)) return <ConsumerHome />;
  return <ConsumerHomePersonal />;
}


function ConsumerEntryRoute() {
  const { user } = useAuth();

  // Authenticated users skip the landing page
  if (user) {
    return <Navigate to="/app/home" replace />;
  }

  return <ConsumerLanding />;
}

const queryClient = new QueryClient();

/**
 * Hook to get the set of paths disabled in the route registry.
 * Returns an empty set if the registry is unhealthy or still loading.
 */
function useDisabledRoutes(): Set<string> {
  const { routes, isLoading, error } = useRouteRegistry();
  if (isLoading || error || routes.length === 0) return new Set();
  const result = validateRouteRegistry(routes);
  if (!result.isHealthy) return new Set(); // Fallback: don't disable anything
  return getDisabledPaths(result.validatedRecords);
}

/** All app routes — rendered inside the /:locale wrapper */
function LocaleRoutes() {
  const { locale: requestedLocale } = useParams<{ locale: string }>();
  const location = useLocation();
  const disabledPaths = useDisabledRoutes();

  if (!requestedLocale || !(SUPPORTED_LOCALES as readonly string[]).includes(requestedLocale)) {
    return <RRNavigate to={`/${getStoredLocale()}${location.pathname}${location.search}${location.hash}`} replace />;
  }

  // Helper: returns null if a route path is disabled in the DB registry
  const guardDisabled = (path: string, element: React.ReactNode) => {
    // Normalize: the DB stores paths like "/:locale/cigars", we check the segment after locale
    if (disabledPaths.has(`/:locale/${path}`) || disabledPaths.has(path)) {
      return <RRNavigate to="/" replace />;
    }
    return element;
  };

  return (
    <LocaleProvider>
      <DynamicFavicon />
      <DynamicManifest />
      <AgeGateWrapper>
        <Suspense fallback={null}>
        <Routes>
          {/* ── Standalone: Onboarding Shells (no PublicLayout, no global AgeGate) ── */}
          <Route path="app/onboarding" element={<AppOnboarding />} />
          <Route path="trade/onboarding" element={<TradeOnboarding />} />
          <Route path="trade/join" element={<TradeJoinRouter />} />
          <Route path="trade/register" element={<TradeRegisterRouter />} />
          <Route path="trade/signin" element={<TradeSignin />} />
          <Route path="trade/join/organisation" element={<TradeJoinOrganisation />} />
          <Route path="trade/join/referral" element={<TradeJoinReferral />} />
          <Route path="trade/join/brand" element={<TradeJoinBrand />} />
          <Route path="trade/select-organisation" element={<TradeSelectOrganisation />} />
          <Route path="trade/select-plan/:organisationId" element={<TradeSelectPlan />} />
          <Route path="trade/invitations/review" element={<TradeInvitationsReview />} />
          <Route path="trade/claim/:entityType/:entityId" element={<TradeClaim />} />
          <Route path="trade/claim/status/:claimId" element={<TradeClaimStatus />} />
          {/* First-visit welcome — deliberately outside PublicLayout: no site
              header, no mobile bottom navigation, no duplicated branding. */}
          <Route path="welcome" element={<Welcome />} />
          {/* Fast Track consumer onboarding (event QR entry point). */}
          <Route path="get-started" element={<GetStarted />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="account/setup" element={<AccountSetup />} />
          <Route path="trade/for-brands" element={<TradeBrands />} />
          <Route path="enterprise" element={<Enterprise />} />
          <Route path="business/contact" element={<Enterprise />} />

          {/* ── Public ── */}
          <Route element={<PublicLayout />}>
            <Route index element={<LocaleHomeEntry />} />
            {/* Home V2 is the primary public homepage at /:locale. The legacy draft URL redirects to it. */}
            <Route path="home-v2" element={<RRNavigate to={`/${requestedLocale}`} replace />} />
            {/* Rollback route: original homepage preserved at /:locale/home-legacy */}
            <Route path="home-legacy" element={<HomeLegacy />} />
            {/* Phase 2 — the previous landing page, preserved in full. */}
            <Route path="why-vitolier" element={<WhyVitolier />} />
            {/* Universal search across every public content type. */}
            <Route path="search" element={<UniversalSearch />} />


            <Route path="cigars" element={<BrowseCigars />} />
            <Route path="discover" element={<Discover />} />
            <Route path="registry" element={<Navigate to="/discover" replace />} />
            <Route path="discover/countries/:country" element={<DiscoverCountry />} />
            <Route path="regions/:slug" element={<RegionDetail />} />
            <Route path="cigars/:id" element={<CigarDetail />} />
            <Route path="brands" element={<BrowseBrands />} />
            <Route path="brands/:id" element={<BrandDetail />} />
            <Route path="vitolas" element={<BrowseVitolas />} />
            <Route path="vitolas/:id" element={<VitolaDetail />} />
            <Route path="lines" element={<BrowseCigarLines />} />
            <Route path="lines/:lineSlug" element={<CigarLineDetail />} />
            <Route path="brands/:brandSlug/lines/:lineSlug" element={<CigarLineDetail />} />
            {/* Legacy "collection" URLs — permanent client redirects to /lines */}
            <Route path="collections" element={<LegacyCollectionRedirect />} />
            <Route path="collections/:collectionSlug" element={<LegacyCollectionRedirect />} />
            <Route path="brands/:brandSlug/collections/:collectionSlug" element={<LegacyCollectionRedirect />} />
            <Route path="manufacturers" element={<BrowseManufacturers />} />
            <Route path="manufacturers/:id" element={<ManufacturerDetail />} />
            {/* Phase 3 — unified public Business Profile preview (flag-gated, noindex) */}
            <Route path="businesses/:organisationId" element={<UnifiedBusinessProfilePage />} />
            <Route path="retailers" element={<BrowseRetailers />} />
            <Route path="retailers/:id" element={<ChannelDetail channelType="retailer" />} />
            <Route path="distributors" element={<BrowseDistributors />} />
            <Route path="distributors/:id" element={<ChannelDetail channelType="distributor" />} />
            <Route path="lounges" element={<BrowseLounges />} />
            <Route path="lounges/:id" element={<ChannelDetail channelType="lounge" />} />
            {/* Vitolier.Club — the members' club (distinct from /clubs, the cigar club directory) */}
            <Route path="club" element={<VitolierClubLanding />} />
            <Route path="clubs" element={<BrowseClubs />} />
            <Route path="clubs/:id" element={<ClubDetail />} />
            <Route path="associations" element={<BrowseAssociations />} />
            <Route path="associations/:id" element={<AssociationDetail />} />
            <Route path="cigar-atlas" element={<CigarAtlas />} />
            <Route path="cigar-atlas/map" element={<CigarAtlasMap />} />
            <Route path="cigar-atlas/suggest" element={<CigarAtlasSuggest />} />
            <Route path="cigar-atlas/collection/:slug" element={<CigarAtlasCollectionDetail />} />
            <Route path="cigar-atlas/city/:slug" element={<CigarAtlasCityCollection />} />

            <Route path="cigar-atlas/:placeType/:slug" element={<CigarAtlasPlaceDetail />} />
            <Route path="about" element={<VitolierPreview />} />
            <Route path="about-vitolier" element={<Navigate to="/about" replace />} />
            <Route path="vitolier" element={<Navigate to="/about" replace />} />
            <Route path="terms" element={<TermsOfUse />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="cookies" element={<CookiesPolicy />} />
            <Route path="brand-guidelines" element={<BrandGuidelines />} />
            <Route path="unsubscribe" element={<Unsubscribe />} />
            <Route path="help" element={<HelpFaq />} />
            <Route path="membership" element={<Plans />} />
            <Route path="why-join" element={<WhyJoin />} />
            <Route path="join" element={<Join />} />
            {/* Member Share & QR referral — member surface + invitation landing */}
            <Route path="share" element={<Share />} />
            <Route path="share/join" element={<ReferralLanding />} />
            <Route path="q/:code" element={<QrLanding />} />
            <Route path="patron/invite/:code" element={<PatronInvite />} />

            <Route path="plans" element={<Navigate to="/membership" replace />} />
            <Route path="membership/success" element={<MembershipSuccess />} />
            <Route path="redeem/discovery" element={<RedeemDiscovery />} />
            <Route path="patron/redeem" element={<PatronActivate />} />
            <Route path="patron/activate/:token" element={<PatronActivate />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="events/past" element={<PastEventsPage />} />

            <Route path="events/register" element={<EventRegister />} />
            <Route path="events/:id" element={<EventDetail />} />
            <Route path="my-events" element={<MyEvents />} />
            <Route path="culture" element={<CulturePage />} />
            <Route path="culture/:slug" element={<CultureArticle />} />

            {/* ── Cigar 101 (educational layer) ── */}
            <Route path="cigar-101" element={<Cigar101Hub />} />
            <Route path="cigar-101/guides/:guideSlug" element={<Cigar101Guide />} />
            <Route path="cigar-101/glossary" element={<Cigar101Glossary />} />
            <Route path="cigar-101/glossary/:termSlug" element={<Cigar101TermDetail />} />

            {/* ── Learn hub (canonical education + editorial home) ── */}
            <Route path="learn" element={<LearnHub />} />
            <Route path="learn/:category" element={<LearnHub />} />

            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="news/:slug" element={<NewsArticle />} />


            {/* ── Vitolier Store (headless Shopify) ── */}
            <Route path="store" element={<StoreLayout />}>
              <Route index element={<StoreHome />} />
              <Route path="books" element={<StoreBooks />} />
              <Route path="books/:slug" element={<StoreBookDetail />} />
              <Route path="vitolier" element={<StoreVitolierCollection />} />
              <Route path="brands" element={<StoreBrandMerchandise />} />
              <Route path="products/:handle" element={<StoreProduct />} />
            </Route>

            <Route path="feed" element={<SocialFeed />} />
            <Route path="post/:messageId" element={<SocialPost />} />
            <Route path="hashtag/:tag" element={<SocialHashtag />} />
            <Route path="auth" element={<Auth />} />
            <Route path="signup" element={<Signup />} />
            <Route path="register" element={<Navigate to="/signup" replace />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="profile" element={<ProfileRedirect />} />
            <Route path="profile/edit" element={<ProfileEdit />} />
            <Route path="profile/:slug" element={<ConsumerProfile />} />
            <Route path="consumer/:slug" element={<ConsumerProfile />} />
            {/* Phase A: legacy Brand onboarding retired — everything routes to the Trade spine */}
            <Route path="brand/onboarding" element={<Navigate to="/trade/register" replace />} />
            <Route path="brand/onboarding/capacity" element={<Navigate to="/trade/register" replace />} />
            <Route path="brand/create" element={<Navigate to="/trade/register" replace />} />
            <Route path="brand/claim" element={<Navigate to="/trade/register" replace />} />
            <Route path="onboarding" element={<Onboarding />} />
            {/* Legacy Trade landing retired — /trade redirects to the canonical /trade/join */}
            <Route path="trade" element={<LegacyTradeLandingRedirect />} />
            <Route path="trade/profile/:channelType/:entityId" element={<TradePublicProfile />} />
            <Route path="app" element={<ConsumerEntryRoute />} />
            <Route path="app-preview" element={<Navigate to="/" replace />} />
            <Route path="access-denied" element={<AccessDenied />} />
          </Route>

          {/* ── Consumer Domain (/app/*) ── */}
          <Route path="app" element={<ConsumerLayout />}>
            <Route index element={<Navigate to="/app/home" replace />} />
            {/* Phase 3 — personalised Home behind the signed_in_home flag;
                the previous dashboard is preserved at /app/home-legacy. */}
            <Route path="home" element={<ConsumerHomeEntry />} />
            <Route path="home-legacy" element={<ConsumerHomeLegacy />} />
            <Route path="content" element={<MyContent />} />

            {/* Phase 4 — Profile and Account Hub */}
            <Route path="account" element={<AccountHub />} />
            <Route path="account/security" element={<AccountSecurity />} />
            <Route path="account/location" element={<AccountLocation />} />
            <Route path="account/share" element={<AccountShare />} />
            <Route path="account/qr" element={<MyQrCodes />} />
            <Route path="account/delete" element={<AccountDelete />} />
            <Route path="account/following" element={<AccountFollowing />} />

            <Route path="discover/world" element={<DiscoverWorld />} />
            <Route path="discover/countries/:country" element={<DiscoverCountry />} />
            <Route path="discover/regions/:slug" element={<RegionDetail />} />
            <Route path="settings" element={<DashboardSettings />} />
            <Route path="reviews" element={<DashboardReviews />} />
            <Route path="humidors" element={<DashboardHumidors />} />
            <Route path="journal" element={<CigarJournal />} />
            <Route path="my-contributions" element={<MyContributions />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="messages" element={<MessagesInbox />} />
            <Route path="messages/new" element={<NewConversation />} />
            <Route path="messages/sessions/new" element={<NewSession />} />
            <Route path="messages/sessions/:sessionId" element={<SessionDetail />} />
            <Route path="messages/:conversationId" element={<ConversationView />} />

            <Route path="feed" element={<SocialFeed />} />
            <Route path="post/:messageId" element={<SocialPost />} />
            <Route path="posts/:messageId" element={<SocialPost />} />
            <Route path="hashtags/:tag" element={<SocialHashtag />} />
            <Route path="trending" element={<Trending />} />
            <Route path="communities" element={<Communities />} />
            <Route path="communities/:idOrSlug" element={<CommunityDetail />} />
            <Route path="settings/notifications" element={<NotificationSettings />} />
            <Route path="smoking-log" element={<DashboardSmokingLog />} />
            <Route path="help" element={<HelpFaq />} />
            <Route path="collections" element={<Navigate to="/app/humidors" replace />} />
            <Route path="insights" element={<DashboardInsights />} />
            <Route path="cigars" element={<BrowseCigars />} />
            <Route path="cigars/:id" element={<CigarProfile />} />
            <Route path="brands" element={<BrowseBrands />} />
            <Route path="brands/:id" element={<BrandDetail />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="events/:id" element={<EventDetail />} />
            <Route path="profile" element={<ProfileRedirect />} />
            <Route path="profile/edit" element={<ProfileEdit />} />
            <Route path="support" element={<AppSupport />} />
            <Route path="support/new-bug" element={<SupportNewBug />} />
            <Route path="support/new-feature" element={<SupportNewFeature />} />
            <Route path="support/features/:id" element={<SupportFeatureDetail />} />
            <Route path="support/bugs/:id" element={<SupportBugDetail />} />
          </Route>

          {/* ── Trade Domain (/trade/*) ── */}
          <Route path="trade/dashboard" element={<TradeLayout />}>
            <Route index element={<TradeDashboard />} />
          </Route>
          <Route path="trade/club-verification" element={<TradeLayout />}>
            <Route index element={<TradeClubVerification />} />
          </Route>
          <Route path="trade/venues" element={<TradeLayout />}>
            <Route index element={<TradeVenues />} />
            <Route path=":placeType/:id" element={<TradeVenueManage />} />
          </Route>
          <Route path="trade/events" element={<TradeLayout />}>
            <Route index element={<TradeEventsWorkspace />} />
            <Route path=":eventId" element={<TradeEventEditor />} />
          </Route>
          <Route path="trade/enquiries" element={<TradeLayout />}>
            <Route index element={<TradeEnquiriesWorkspace />} />
            <Route path=":enquiryId" element={<TradeEnquiryDetail />} />
          </Route>
          <Route path="trade/claims" element={<TradeLayout />}>
            <Route index element={<TradeClaims />} />
          </Route>
          <Route path="trade/entity/:entitySlug" element={<TradeLayout />}>
            <Route index element={<TradeEntityManage />} />
          </Route>

          <Route path="trade/brands" element={<TradeLayout />}>
            {/* My Brands — organisation-scoped Brand portfolio (NOT the Registry).
                Registry browsing remains available at /brands and /app/brands. */}
            <Route index element={<MyBrandsPortfolio />} />
            <Route path=":brandId" element={<BrandOwnerLayout />}>
              <Route index element={<BrandPortalDashboard />} />
              <Route path="content" element={<BrandPortalContent />} />
              <Route path="cigars" element={<BrandPortalCigars />} />
              <Route path="cigars/:cigarId/relationships" element={<BrandPortalCigarRelationships />} />
              <Route path="media" element={<BrandPortalMedia />} />
              <Route path="events" element={<BrandPortalEvents />} />
              <Route path="relationships" element={<BrandPortalRelationships />} />
              <Route path="corrections" element={<BrandPortalCorrections />} />
              <Route path="corrections/:requestId" element={<BrandPortalCorrectionDetail />} />
              <Route path="analytics" element={<BrandPortalAnalytics />} />
              <Route path="subscription" element={<BrandPortalSubscription />} />
            </Route>
          </Route>
          <Route path="trade/cigars" element={<TradeLayout />}>
            {/* My Cigars — organisation-scoped Cigar portfolio (NOT the Registry). */}
            <Route index element={<MyCigarsPortfolio />} />
          </Route>
          <Route path="trade/crm" element={<TradeLayout />}>
            <Route index element={<TradeCrm />} />
          </Route>
          <Route path="trade/analytics" element={<Navigate to="/trade/intelligence" replace />} />
          <Route path="trade/intelligence" element={<TradeLayout />}>
            <Route index element={<TradeIntelligenceCentre />} />
            <Route path="relationships" element={<TradeRelationshipIntelligence />} />
          </Route>
          <Route path="trade/organisation" element={<TradeLayout />}>
            <Route index element={<TradeOrganisation />} />
          </Route>
          <Route path="trade/content" element={<TradeLayout />}>
            <Route index element={<TradeContent />} />
          </Route>
          <Route path="trade/discovery" element={<TradeLayout />}>
            <Route index element={<TradeDiscovery />} />
          </Route>
          {/* Business Profile Shell (Phase 1) — additive, legacy routes untouched */}
          <Route path="trade/manage/:entityType/:entityId" element={<TradeLayout />}>
            <Route index element={<TradeManageShell />} />
            <Route path=":tab" element={<TradeManageShell />} />
          </Route>
          {/* Phase 3 — shared Business Profile management shell (flag-gated) */}
          <Route path="business/manage/:organisationId" element={<BusinessManagementShell />} />
          <Route path="business/manage/:organisationId/:section" element={<BusinessManagementShell />} />
          <Route path="trade/invitations/accept" element={<TradeInvitationAccept />} />


          {/* ── Legacy Redirects ── */}
          <Route path="dashboard" element={<Navigate to="/app/home" replace />} />
          <Route path="dashboard/settings" element={<Navigate to="/app/settings" replace />} />
          <Route path="dashboard/reviews" element={<Navigate to="/app/reviews" replace />} />
          <Route path="dashboard/humidors" element={<Navigate to="/app/humidors" replace />} />
          <Route path="dashboard/smoking-log" element={<Navigate to="/app/smoking-log" replace />} />
          <Route path="dashboard/collections" element={<Navigate to="/app/humidors" replace />} />
          <Route path="dashboard/insights" element={<Navigate to="/app/insights" replace />} />
          <Route path="brand-portal/:brandId/*" element={<LegacyBrandPortalRedirect />} />
          <Route path="brand/dashboard" element={<Navigate to="/trade/dashboard" replace />} />
          <Route path="retailer/dashboard" element={<Navigate to="/trade/dashboard" replace />} />

          {/* ── Admin ── */}
          {/* ── Vitolier.Club member area ── */}
          <Route path="club/member" element={<ClubMemberLayout />}>
            <Route index element={<ClubOverview />} />
            <Route path="card" element={<ClubCardPage />} />
            <Route path="benefits" element={<ClubBenefitsPage />} />
            <Route path="events" element={<ClubEventsPage />} />
            <Route path="chapters" element={<ClubChaptersPage />} />
            <Route path="history" element={<ClubHistoryPage />} />
            <Route path="preferences" element={<ClubPreferencesPage />} />
          </Route>

          <Route path="admin" element={<AdminLayout />}>
            <Route path="club" element={<AdminVitolierClub />} />
            <Route path="qr-reports" element={<AdminQrReports />} />
            <Route index element={<AdminDashboard />} />
            <Route path="content" element={<AdminContentSharing />} />
            <Route path="registry" element={<AdminRegistryApprovals />} />
            <Route path="registry/corrections" element={<AdminRegistryCorrections />} />
            <Route path="registry-admin" element={<RRNavigate to="/admin/registry" replace />} />

            <Route path="registry/approvals" element={<RRNavigate to="/admin/registry" replace />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="brands/hierarchy" element={<AdminBrandHierarchy />} />
            <Route path="brands/:id" element={<AdminCrmBrandDetail />} />
            <Route path="manufacturers" element={<AdminManufacturers />} />
            <Route path="cigars" element={<AdminCigarsPage />} />
            <Route path="cigar-registry" element={<AdminCigarRegistry />} />
            <Route path="retailers" element={<AdminRetailers />} />
            <Route path="distributors" element={<AdminDistributors />} />
            <Route path="lounges" element={<AdminLounges />} />
            <Route path="relationships" element={<AdminRelationships />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="culture" element={<AdminCulture />} />
            <Route path="cigar-101" element={<AdminCigar101 />} />
            {/* Unified hubs (5C) */}
            <Route path="claims" element={<AdminClaimsHub />} />
            <Route path="submissions" element={<AdminSubmissionsHub />} />
            <Route path="moderation" element={<AdminModerationHub />} />
            <Route path="insights" element={<AdminInsightsHub />} />
            <Route path="subscribers" element={<AdminSubscribersHub />} />
            <Route path="crm" element={<AdminCrmHub />} />
            <Route path="work-hub" element={<AdminWorkHubUnified />} />

            {/* New 5C pages */}
            <Route path="businesses-console" element={<AdminBusinessesConsole />} />
            <Route path="organisations" element={<AdminOrganisations />} />
            <Route path="organisations/:id" element={<AdminOrganisationDetail />} />
            <Route path="sites" element={<AdminSites />} />
            <Route path="entitlements" element={<AdminEntitlements />} />
            <Route path="email-ops" element={<AdminEmailOps />} />
            <Route path="notifications" element={<AdminNotificationsBroadcast />} />
            <Route path="security" element={<AdminSecurityFindings />} />
            <Route path="users-roles" element={<AdminUsersRoles />} />
            <Route path="classification-review" element={<AdminClassificationReview />} />

            <Route path="billing" element={<AdminBilling />} />
            <Route path="memberships" element={<AdminMemberships />} />
            <Route path="retailer-claims" element={<AdminClaimsHub />} />
            <Route path="ownership" element={<AdminOwnership />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="external-reviews" element={<AdminExternalReviews />} />
            <Route path="external-reviews/:id" element={<AdminExternalReviewDetail />} />
            <Route path="submissions/:id" element={<AdminSubmissionDetail />} />
            <Route path="brand-submissions" element={<AdminSubmissionsHub />} />
            <Route path="brand-claims" element={<AdminClaimsHub />} />
            <Route path="duplicates" element={<AdminDuplicates />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="vitolas" element={<AdminVitolas />} />
            <Route path="countries" element={<AdminCountries />} />
            <Route path="countries/contributions" element={<AdminCountryContributionsPage />} />
            <Route path="taxonomy" element={<AdminTaxonomy />} />
            <Route path="consumers" element={<AdminConsumers />} />
            <Route path="consumers/:id" element={<AdminConsumerDetail />} />
            <Route path="crm/contacts/new" element={<AdminCrmContactNew />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="imports" element={<AdminImports />} />

            {/* Legacy → unified redirects */}
            <Route path="subscribers-dashboard" element={<RRNavigate to="/admin/subscribers?tab=kpis" replace />} />
            <Route path="version-history" element={<RRNavigate to="/admin/insights?tab=versions" replace />} />
            <Route path="data-quality" element={<RRNavigate to="/admin/insights?tab=data-quality" replace />} />
            <Route path="platform-insights" element={<RRNavigate to="/admin/insights?tab=platform" replace />} />
            <Route path="compliance-moderation" element={<RRNavigate to="/admin/moderation?category=compliance" replace />} />
            <Route path="users" element={<RRNavigate to="/admin/users-roles" replace />} />
            <Route path="work-hub/mine" element={<RRNavigate to="/admin/work-hub?tab=mine" replace />} />
            <Route path="work-hub/templates" element={<RRNavigate to="/admin/work-hub?tab=templates" replace />} />
            <Route path="crm/command" element={<RRNavigate to="/admin/crm?tab=command" replace />} />
            <Route path="crm/pipeline" element={<RRNavigate to="/admin/crm?tab=pipeline" replace />} />
            <Route path="crm/requests" element={<RRNavigate to="/admin/crm?tab=requests" replace />} />

            <Route path="media" element={<AdminMedia />} />
            <Route path="media-health" element={<AdminMediaHealth />} />
            <Route path="job-health" element={<AdminJobHealth />} />
            <Route path="businesses" element={<AdminBusinesses />} />
            <Route path="businesses/:id" element={<AdminBusinessDetail />} />
            <Route path="retailer-monetisation" element={<AdminRetailerMonetisation />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="crm/brands/:id" element={<AdminCrmBrandDetail />} />
            <Route path="sitemap" element={<AdminSitemap />} />
            <Route path="app-icons" element={<AdminAppIcons />} />
            <Route path="firecrawl-import" element={<AdminFirecrawlImport />} />
            <Route path="regulatory" element={<AdminRegulatory />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="store" element={<AdminStore />} />
            <Route path="cigar-lines" element={<AdminCigarLines />} />
            <Route path="collections" element={<Navigate to="/admin/cigar-lines" replace />} />
            <Route path="cigar-atlas" element={<AdminCigarAtlas />} />
            <Route path="cigar-atlas/import" element={<AdminCigarAtlasImport />} />
            <Route path="cigar-atlas/import/firecrawl" element={<AdminCigarAtlasFirecrawlImport />} />
            <Route path="cigar-atlas/import/excel" element={<AdminCigarAtlasExcelImport />} />
            <Route path="cigar-atlas/collections" element={<AdminCigarAtlasCollections />} />
            <Route path="cigar-atlas/city-collections" element={<AdminCigarAtlasCityCollections />} />

            <Route path="cigar-atlas/import/references" element={<AdminCigarAtlasReferences />} />
            <Route path="cigar-atlas/intelligence-review" element={<AdminCigarAtlasIntelligenceReview />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <LazyVitolierChatbot />
        </Suspense>
      </AgeGateWrapper>
    </LocaleProvider>
  );
}

/** Redirect legacy /:locale/trade to /:locale/trade/join, preserving query + hash */
function LegacyTradeLandingRedirect() {
  const location = useLocation();
  const seg = location.pathname.split("/")[1];
  const locale = (SUPPORTED_LOCALES as readonly string[]).includes(seg) ? seg : getStoredLocale();
  return <RRNavigate to={`/${locale}/trade/join${location.search}${location.hash}`} replace />;
}

/** Redirect legacy /brand-portal/:brandId/* to /trade/brands/:brandId/* */
function LegacyBrandPortalRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/\/brand-portal\//, "/trade/brands/");
  return <RRNavigate to={newPath} replace />;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ComplianceProvider>
              <AnalyticsProvider />
              <ReferralCapture />
              <TestAccountBanner />

              <GlobalErrorReportListener />
              <Suspense fallback={<div className="min-h-screen" aria-hidden />}>
                <Routes>
                  {/* Redirect bare "/" to stored locale */}
                  <Route path="/" element={<LocaleRedirect />} />

                  {/* Standalone preview pages — wrapped in LocaleProvider for i18n */}
                  <Route path="/business-preview" element={<LocaleProvider><BusinessPreview /></LocaleProvider>} />
                  <Route path="/trade-preview" element={<LocaleProvider><TradePreview /></LocaleProvider>} />
                  <Route path="/vitolier-preview" element={<RRNavigate to={`/${getStoredLocale()}/about`} replace />} />
                  {/* Legacy mobile homepage retired — /app-preview redirects to the canonical homepage */}
                  <Route path="/app-preview" element={<LocaleRedirect />} />

                  {/* ── Research Operations: isolated, no locale prefix, no path back into Vitolier ── */}
                  <Route path="/research" element={<RRNavigate to="/research/dashboard" replace />} />
                  <Route path="/research/login" element={<ResearchLogin />} />
                  <Route path="/research/invite/:token" element={<ResearchInvitePage />} />
                  <Route path="/research/forgot-password" element={<ResearchPasswordPage />} />
                  <Route path="/research/access-denied" element={<ResearchAccessDeniedPage />} />
                  <Route
                    path="/research"
                    element={
                      <ResearchGuard>
                        <LocaleProvider urlPrefixed={false}><ResearchLayoutShell /></LocaleProvider>
                      </ResearchGuard>
                    }
                  >
                    <Route path="dashboard" element={<ResearchDashboard />} />
                    <Route path="business-atlas" element={<ResearchBusinessWorkstreamPage />} />
                    <Route path="brands" element={<ResearchRegistryWorkPage recordType="brand" />} />
                    <Route path="cigar-lines" element={<ResearchRegistryWorkPage recordType="cigar_line" />} />
                    <Route path="cigars" element={<ResearchRegistryWorkPage recordType="cigar" />} />
                    <Route path="my-work" element={<ResearchMyWorkPage />} />
                    <Route path="work/:workItemId" element={<ResearchWorkItemPage />} />
                    <Route path="profile" element={<ResearchProfilePage />} />
                    <Route path="password" element={<ResearchPasswordPage />} />
                    <Route path="help" element={<ResearchHelpPage />} />
                    <Route
                      path="projects"
                      element={<ResearchGuard require="manage"><ResearchProjectsPage /></ResearchGuard>}
                    />
                    <Route
                      path="projects/:projectId"
                      element={<ResearchGuard require="manage"><ResearchProjectDetailPage /></ResearchGuard>}
                    />
                    <Route
                      path="ai"
                      element={<ResearchGuard require="manage"><ResearchAiRegistryPage /></ResearchGuard>}
                    />
                    <Route
                      path="review"
                      element={<ResearchGuard require="review"><ResearchReviewPage /></ResearchGuard>}
                    />
                    <Route
                      path="users"
                      element={<ResearchGuard require="admin"><ResearchUsersPage /></ResearchGuard>}
                    />
                    <Route path="*" element={<RRNavigate to="/research/dashboard" replace />} />
                  </Route>


                  {/* Locale-prefixed routes */}
                  <Route path="/:locale/*" element={<LocaleRoutes />} />

                  {/* Catch-all for non-locale paths → redirect with locale prefix */}
                  <Route path="*" element={<LocaleRedirect />} />
                </Routes>
              </Suspense>
            </ComplianceProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);


export default App;
